import { useTranslation } from 'react-i18next'
import { Minus, Plus, Tag, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
} from '@/components/ui/form-control-styles'
import { CurrencyNumberInput } from '@/components/ui/currency-input'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ServiceItemPicker,
  type ServiceItemSelection,
} from '@/components/shared'
import {
  useInfiniteServiceItemCatalog,
  useTaxRateOptions,
} from '@/hooks/useSettings'
import {
  TAX_RATES,
  computeUnitPriceFromLineTotal,
  computeLineTotal,
  computeTotals,
  emptyLineItem,
} from '@/lib/lineItems'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { LineItem } from '@/types'

// Patch from a ServiceItemPicker selection (in-row catalogue choice, #27).
function patchFromSelection(sel: ServiceItemSelection): Partial<LineItem> {
  return {
    service_item_id: sel.id,
    description: sel.name,
    service_description: sel.description,
    image_file_id: sel.image_file_id,
    unit_price: sel.unit_price,
    tax_rate: sel.tax_rate ?? 20,
    image_url: sel.image,
  }
}

function LibraryIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 16 16'
      fill='none'
      aria-hidden='true'
      className={className}
    >
      <path
        d='M2.12 10.56 8.5 12.6l5.38-1.7-6.39-2.05-5.37 1.7Z'
        fill='#F5C650'
      />
      <path
        d='m2.12 10.56 1.14 2.22 6.37 2.07L8.5 12.6l-6.38-2.04Z'
        fill='#D89D00'
      />
      <path
        d='m13.88 10.9-1.15 2.21-6.37 1.74L8.5 12.6l5.38-1.7Z'
        fill='#F7D978'
      />
      <path
        d='M3.08 6.93 8.5 8.67l4.63-1.46-5.42-1.74-4.63 1.46Z'
        fill='#FF7B70'
      />
      <path d='m3.08 6.93.97 1.9 5.42 1.74-.97-1.9-5.42-1.74Z' fill='#D7443A' />
      <path
        d='m13.13 7.2-.98 1.91-5.42 1.46.97-1.9 5.43-1.47Z'
        fill='#FFAAA1'
      />
      <path
        d='M4.18 3.5 8.8 4.98l3.94-1.24-4.62-1.48L4.18 3.5Z'
        fill='#18A7F5'
      />
      <path d='m4.18 3.5.82 1.6 4.62 1.49-.82-1.61L4.18 3.5Z' fill='#0A6BB5' />
      <path
        d='m12.74 3.74-.83 1.61L7.3 6.58l.82-1.6 4.62-1.24Z'
        fill='#56C4FF'
      />
      <path
        d='M11.53 1.8 13 2.96 9.86 3.95 8.39 2.78l3.14-.98Z'
        fill='#0A6BB5'
      />
      <path d='m9.86 3.95.98 1.9 1.48-1.16-.98-1.9-1.48 1.16Z' fill='#E0A446' />
    </svg>
  )
}

interface Props {
  items: LineItem[]
  onChange: (items: LineItem[]) => void
  // Surfaced when the user attempts to save with no items (AC-2.5.2-010 etc).
  error?: string | null
  /** Field-level invalid states keyed by zero-based line index. */
  itemErrors?: Record<number, { unit_price?: boolean }>
  // Marks the section heading with a required asterisk (at least one line is
  // mandatory, e.g. quotes/invoices).
  required?: boolean
  /** Hide the built-in section heading when the parent renders one (e.g. quote form). */
  hideHeading?: boolean
  /** Decimal places for currency display (default 0). */
  fractionDigits?: number
  /** Hide the auto subtotal / VAT / total recap block. */
  hideTotals?: boolean
  /** Quote form uses a dedicated Figma variant without affecting invoices. */
  variant?: 'default' | 'quote'
  /** Open the full service-item library drawer. */
  onLibraryClick: () => void
  /** Override the default inline add-row action (quote form opens a drawer). */
  onAddItemClick?: () => void
  /** Optional synthetic discount row rendered inside the quote line-items table. */
  tradeDiscountRow?: {
    title: string
    description: string
    amount: number
    actionLabel?: string
    onAction?: () => void
  } | null
}

// Repeating line-items grid shared by Create/Edit quote (2.5.2) and Create
// invoice (2.6.3). Auto-calculates line totals + running totals. Lines are
// chosen from the Settings catalogue via ServiceItemPicker (feedback #27), plus
// the full service-item library.
export function LineItemsEditor({
  items,
  onChange,
  error,
  itemErrors,
  required = false,
  hideHeading = false,
  fractionDigits = 0,
  hideTotals = false,
  variant = 'default',
  onLibraryClick,
  onAddItemClick,
  tradeDiscountRow = null,
}: Props) {
  const { t } = useTranslation('billing')
  const isQuoteVariant = variant === 'quote'
  // Warm the shared catalogue while the form renders so opening the library
  // never has to wait for its first request.
  useInfiniteServiceItemCatalog()
  // Active tax rates from Settings > Tax rates (2.11.8 BR05); fall back to the
  // common French VAT rates until they load or if none are configured.
  const { data: taxOptions } = useTaxRateOptions()
  const configuredRates = taxOptions?.data?.map(r => r.rate)
  const taxRates: number[] =
    configuredRates && configuredRates.length > 0
      ? Array.from(new Set(configuredRates))
      : [...TAX_RATES]
  const update = (idx: number, patch: Partial<LineItem>) => {
    const next = items.map((it, i) => {
      if (i !== idx) return it
      const merged = { ...it, ...patch }
      merged.line_total = computeLineTotal(merged)
      return merged
    })
    onChange(next)
  }

  const updateLineTotal = (idx: number, nextLineTotal: number) => {
    const next = items.map((it, i) => {
      if (i !== idx) return it
      const unitPrice = computeUnitPriceFromLineTotal(it, nextLineTotal)
      const merged = { ...it, unit_price: unitPrice }
      merged.line_total = computeLineTotal(merged)
      return merged
    })
    onChange(next)
  }

  const add = () => onChange([...items, emptyLineItem()])
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx))

  const isManual = (it: LineItem) => !it.service_item_id && !!it.description

  const totals = computeTotals(items)

  const fmt = (value: number) => formatCurrency(value, 'EUR', fractionDigits)

  const figmaFieldBaseClass = cn(
    'h-10 rounded-xl border px-4 py-3 leading-[1.4] shadow-none',
    FORM_CONTROL_BASE_CLASS
  )
  const figmaFieldClass = cn(figmaFieldBaseClass, FORM_CONTROL_FOCUS_CLASS)
  const figmaSelectClass = cn(
    figmaFieldBaseClass,
    FORM_CONTROL_SELECT_OPEN_CLASS
  )
  const quoteServicePickerClass =
    'min-h-10 rounded-none border-0 bg-transparent px-0 py-0 text-left shadow-none hover:bg-transparent focus:ring-0 [&>svg]:hidden [&>span]:min-w-0 [&>span]:flex-1 [&>span]:line-clamp-none [&>span]:whitespace-normal'
  const addButtonClass =
    'h-10 rounded-xl border-dashed border-[#b0b5c3] bg-white px-3 text-xs font-medium text-[#171a20] hover:bg-[#fbfbfc]'
  const libraryButtonClass =
    'h-10 rounded-xl border border-[#ff5a1f] bg-[#fff1ea] px-4 text-xs font-medium text-[#ff5a1f] hover:bg-[#ffe7db]'
  const actionButtons = (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3',
        hideHeading && 'w-full pt-4'
      )}
    >
      <Button
        type='button'
        variant='outline'
        size='sm'
        className={cn(hideHeading && addButtonClass)}
        onClick={() => (onAddItemClick ? onAddItemClick() : add())}
      >
        <Plus className='mr-2 h-4 w-4' /> {t('lineItems.addItem')}
      </Button>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className={cn(hideHeading && libraryButtonClass)}
        onClick={onLibraryClick}
      >
        <LibraryIcon className='mr-2 h-4 w-4' /> {t('lineItems.library')}
      </Button>
    </div>
  )

  const headClass = cn(
    'normal-case tracking-normal',
    hideHeading &&
      isQuoteVariant &&
      'h-10 px-6 text-[10px] font-medium uppercase tracking-normal text-[#3d434e]'
  )
  const quoteHeadClass =
    'h-10 px-3 py-2 text-[10px] font-medium leading-[14px] uppercase tracking-normal text-[#3d434e]'
  const quoteCellClass = 'h-16 px-3 py-2'
  const quoteHeaderFillClass =
    hideHeading && isQuoteVariant ? 'bg-[#f4f5f8]' : undefined

  return (
    <div className='space-y-3'>
      {!hideHeading && (
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <h3 className='text-sm font-semibold'>{t('lineItems.heading')}</h3>
          {actionButtons}
        </div>
      )}

      {error && <p className='text-sm text-destructive'>{error}</p>}

      <Table
        className={cn(
          '[&_tbody_tr]:!bg-[#FFFFFF]',
          hideHeading &&
            isQuoteVariant &&
            'overflow-hidden [&_tbody_tr]:border-[#e7e9ef] [&_thead_tr]:border-[#e7e9ef]'
        )}
      >
        <TableHeader>
          <TableRow>
            <TableHead
              className={cn(
                isQuoteVariant ? 'min-w-[280px]' : 'w-[40%]',
                headClass,
                quoteHeaderFillClass,
                hideHeading && isQuoteVariant && quoteHeadClass
              )}
            >
              {t('lineItems.serviceItem')}
              {required && <span className='text-destructive'> *</span>}
            </TableHead>
            <TableHead
              className={cn(
                isQuoteVariant ? 'w-[144px] min-w-[144px]' : 'w-[12%]',
                headClass,
                quoteHeaderFillClass,
                hideHeading && isQuoteVariant && quoteHeadClass
              )}
            >
              {t('lineItems.quantity')}
              {required && <span className='text-destructive'> *</span>}
            </TableHead>
            <TableHead
              className={cn(
                isQuoteVariant ? 'w-[144px] min-w-[144px]' : 'w-[16%]',
                headClass,
                quoteHeaderFillClass,
                hideHeading && isQuoteVariant && quoteHeadClass
              )}
            >
              {t('lineItems.unitPrice')}
              {required && <span className='text-destructive'> *</span>}
            </TableHead>
            <TableHead
              className={cn(
                isQuoteVariant ? 'w-[144px] min-w-[144px]' : 'w-[14%]',
                headClass,
                quoteHeaderFillClass,
                hideHeading && isQuoteVariant && quoteHeadClass
              )}
            >
              {t('lineItems.taxRate')}
              {required && <span className='text-destructive'> *</span>}
            </TableHead>
            <TableHead
              className={cn(
                isQuoteVariant
                  ? 'w-[144px] min-w-[144px] text-left'
                  : 'w-[14%] text-right',
                headClass,
                quoteHeaderFillClass,
                hideHeading && isQuoteVariant && quoteHeadClass
              )}
            >
              {t('lineItems.lineTotal')}
            </TableHead>
            <TableHead
              className={cn(
                isQuoteVariant ? 'w-10 min-w-10' : 'w-[4%]',
                quoteHeaderFillClass,
                hideHeading && isQuoteVariant && 'h-10 px-2 py-2'
              )}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && !hideHeading && (
            <TableRow>
              <TableCell
                colSpan={6}
                className='py-6 text-center text-muted-foreground'
              >
                {t('lineItems.empty')}
              </TableCell>
            </TableRow>
          )}
          {items.map((it, idx) => (
            <TableRow
              key={idx}
              className={cn(hideHeading && isQuoteVariant && '')}
            >
              <TableCell
                className={cn(hideHeading && isQuoteVariant && quoteCellClass)}
              >
                <div className='min-w-0 space-y-1'>
                  {isManual(it) ? (
                    <Input
                      value={it.description}
                      placeholder={t('lineItems.descriptionPlaceholder')}
                      maxLength={255}
                      className={cn(figmaFieldClass)}
                      onChange={e =>
                        update(idx, {
                          description: e.target.value,
                          service_item_id: null,
                        })
                      }
                    />
                  ) : (
                    <ServiceItemPicker
                      value={it.service_item_id ?? null}
                      // Once a catalogue item has been added, the service cell
                      // becomes display-only. Replacing it is intentionally done
                      // by removing the line and adding a new item instead.
                      disabled={Boolean(it.service_item_id)}
                      onSelect={sel => update(idx, patchFromSelection(sel))}
                      placeholder={t('lineItems.serviceItemPlaceholder')}
                      fractionDigits={fractionDigits}
                      selectedSecondary={
                        isQuoteVariant ? 'description' : 'price'
                      }
                      selectedName={it.description}
                      selectedDescription={it.service_description}
                      imageClassName={isQuoteVariant ? 'rounded-lg' : undefined}
                      className={cn(
                        isQuoteVariant
                          ? quoteServicePickerClass
                          : figmaFieldClass,
                        it.service_item_id &&
                          'disabled:cursor-default disabled:opacity-100'
                      )}
                    />
                  )}
                </div>
              </TableCell>
              <TableCell
                className={cn(hideHeading && isQuoteVariant && quoteCellClass)}
              >
                {isQuoteVariant ? (
                  <div className='inline-flex h-10 w-[119px] items-center justify-center gap-2 rounded-xl bg-[#f4f5f8] p-3 text-[#171a20]'>
                    <button
                      type='button'
                      className='inline-flex size-4 shrink-0 items-center justify-center rounded hover:bg-white'
                      onClick={() =>
                        update(idx, {
                          quantity: Math.max(1, (it.quantity || 1) - 1),
                        })
                      }
                    >
                      <Minus className='size-4' />
                    </button>
                    <span className='min-w-0 flex-1 text-center text-sm font-regular leading-[1.4]'>
                      {it.quantity || 1}
                    </span>
                    <button
                      type='button'
                      className='inline-flex size-4 shrink-0 items-center justify-center rounded hover:bg-white'
                      onClick={() =>
                        update(idx, {
                          quantity: Math.max(1, (it.quantity || 1) + 1),
                        })
                      }
                    >
                      <Plus className='size-4' />
                    </button>
                  </div>
                ) : (
                  <Input
                    type='number'
                    min={1}
                    step={1}
                    value={it.quantity}
                    className={cn(figmaFieldClass)}
                    onChange={e =>
                      update(idx, { quantity: Number(e.target.value) })
                    }
                  />
                )}
              </TableCell>
              <TableCell
                className={cn(hideHeading && isQuoteVariant && quoteCellClass)}
              >
                <CurrencyNumberInput
                  value={it.unit_price}
                  emptyAsZero={false}
                  showEuroPrefix={!isQuoteVariant}
                  aria-invalid={Boolean(itemErrors?.[idx]?.unit_price)}
                  className={cn(
                    figmaFieldClass,
                    itemErrors?.[idx]?.unit_price && 'border-destructive'
                  )}
                  onValueChange={amount => update(idx, { unit_price: amount })}
                />
              </TableCell>
              <TableCell
                className={cn(hideHeading && isQuoteVariant && quoteCellClass)}
              >
                <Select
                  value={String(it.tax_rate)}
                  onValueChange={v => update(idx, { tax_rate: Number(v) })}
                >
                  <SelectTrigger
                    className={cn(
                      figmaSelectClass,
                      isQuoteVariant && 'w-[119px]'
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Always include the row's current rate so an existing
                        line keeps its value even if that rate was deactivated. */}
                    {Array.from(new Set([...taxRates, it.tax_rate ?? 0]))
                      .sort((a, b) => a - b)
                      .map(r => (
                        <SelectItem key={r} value={String(r)}>
                          {r}%
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell
                className={cn(
                  'text-right font-medium',
                  hideHeading &&
                    isQuoteVariant &&
                    cn(quoteCellClass, 'text-left text-base text-[#171a20]')
                )}
              >
                <CurrencyNumberInput
                  value={it.line_total}
                  showEuroPrefix={!isQuoteVariant}
                  className={cn(figmaFieldClass, 'text-[#171a20]')}
                  onValueChange={amount => updateLineTotal(idx, amount ?? 0)}
                />
              </TableCell>
              <TableCell
                className={cn(
                  hideHeading && isQuoteVariant && 'h-16 w-10 px-2 py-2'
                )}
              >
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className={cn(
                    'h-8 w-8 text-destructive',
                    hideHeading &&
                      isQuoteVariant &&
                      'size-6 rounded-lg bg-white p-1 text-[#d63b33] hover:bg-[#fff1ea] hover:text-[#d63b33]'
                  )}
                  title={t('lineItems.remove')}
                  onClick={() => remove(idx)}
                >
                  <X className='size-4' />
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {hideHeading && isQuoteVariant && tradeDiscountRow ? (
            <TableRow>
              <TableCell colSpan={6} className='px-3 pb-3 pt-2'>
                <div className='flex flex-wrap items-center gap-3 rounded-2xl bg-[#ecfdf5] px-4 py-3'>
                  <div className='flex min-w-0 flex-1 items-start gap-3'>
                    <span className='mt-0.5 flex size-4 shrink-0 items-center justify-center text-[#272c36]'>
                      <Tag className='size-4' />
                    </span>
                    <div className='min-w-0'>
                      <p className='truncate text-sm font-medium uppercase leading-6 tracking-[0.01em] text-[#272c36]'>
                        {tradeDiscountRow.title}
                      </p>
                      <p className='text-xs font-normal leading-5 text-[#272c36]'>
                        {tradeDiscountRow.description}
                      </p>
                    </div>
                  </div>

                  <div className='ml-auto flex items-center gap-3'>
                    <span className='text-right font-heading text-[20px] font-bold leading-6 tracking-[-0.02em] text-[#059669]'>
                      -{fmt(tradeDiscountRow.amount)}
                    </span>
                    {tradeDiscountRow.onAction ? (
                      <button
                        type='button'
                        className='text-xs font-medium text-[#555b6a] transition-colors hover:text-[#171a20]'
                        onClick={tradeDiscountRow.onAction}
                      >
                        {tradeDiscountRow.actionLabel ?? ''}
                      </button>
                    ) : null}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      {hideHeading && actionButtons}

      {/* Totals / tax summary (auto). */}
      {!hideTotals &&
        (hideHeading ? (
          <div className='flex w-full justify-end overflow-hidden border-y border-border'>
            <div className='flex min-w-0 flex-1 flex-col'>
              <div className='flex h-12 items-center justify-end bg-[#f9f9f9] px-3'>
                <span className='text-sm font-semibold text-[#757575]'>
                  {t('lineItems.subtotal')}
                </span>
              </div>
              <div className='flex h-12 items-center justify-end border-b border-border bg-[#f9f9f9] px-3'>
                <span className='text-sm font-semibold text-[#757575]'>
                  {t('lineItems.tax')}
                </span>
              </div>
              <div className='flex h-14 items-center justify-end border-b border-border bg-[#f9f9f9] px-3 py-4'>
                <span className='text-base font-semibold text-foreground'>
                  {t('lineItems.total')}
                </span>
              </div>
            </div>
            <div className='flex w-[200px] shrink-0 flex-col'>
              <div className='flex h-12 items-center justify-end bg-[#f9f9f9] pl-3 pr-14'>
                <span className='text-sm text-foreground'>
                  {fmt(totals.subtotal)}
                </span>
              </div>
              <div className='flex h-12 items-center justify-end border-b border-border bg-[#f9f9f9] pl-3 pr-14'>
                <span className='text-sm text-foreground'>
                  {fmt(totals.tax_total)}
                </span>
              </div>
              <div className='flex h-14 items-center justify-end border-b border-border bg-[#f9f9f9] pl-3 pr-14 py-4'>
                <span className='text-base font-semibold text-foreground'>
                  {fmt(totals.total)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className='ml-auto w-full max-w-xs space-y-1 text-sm'>
            <div className='flex justify-between'>
              <span className='text-muted-foreground'>
                {t('lineItems.subtotal')}
              </span>
              <span>{fmt(totals.subtotal)}</span>
            </div>
            <div className='flex justify-between'>
              <span className='text-muted-foreground'>
                {t('lineItems.tax')}
              </span>
              <span>{fmt(totals.tax_total)}</span>
            </div>
            <div className='flex justify-between border-t border-border pt-1 text-base font-semibold'>
              <span>{t('lineItems.total')}</span>
              <span>{fmt(totals.total)}</span>
            </div>
          </div>
        ))}
    </div>
  )
}

export default LineItemsEditor
